import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

import CopyTextInput from '@dashboard/components/form/fields/CopyTextInput';
import LoadingIndicator from '@dashboard/components/loading/LoadingIndicator';
import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import PermissionTooltip from '@dashboard/components/PermissionTooltip';
import BaseSettingCard from '@dashboard/components/platform-settings/BaseSettingCard';
import { useApiKeysQuery } from '@dashboard/generated/wp/platform';
import { useApiKeyNewHref } from '@dashboard/hooks/route/hrefs';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { ListAltOutlined } from '@mui/icons-material';
import { Button, Divider, Link, Stack, Typography } from '@mui/material';

import { usePermission } from './hooks';

export default function ApiKeysListCard() {
  const { data } = useApiKeysQuery({ fetchPolicy: 'network-only' });
  const platformId = useCurrentPlatformId();
  const newApiKeyHref = useApiKeyNewHref();
  const apiKeys = data?.ApiKeys.nodes;
  const createApiKeyPermission = usePermission('createApiKey');
  const navigate = useNavigate();

  const renderChildren = () => {
    if (apiKeys === undefined) {
      return <LoadingIndicator />;
    }

    if (apiKeys.length === 0) {
      return (
        <Typography color="text.secondary">
          You haven't created an API key yet. To learn more, we recommend following our{' '}
          <Link href="https://www.usewaypoint.com/docs/quickstart" target="_blank">
            quick start guide
          </Link>
          .
        </Typography>
      );
    }

    return (
      <Stack spacing={3}>
        {apiKeys.map(({ id, password, name }, i) => (
          <Fragment key={i}>
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Typography>{name}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <CopyTextInput label="Username" value={id} />
                <CopyTextInput label="Password" value={password} />
              </Stack>
            </Stack>
            <Divider />
          </Fragment>
        ))}
        <ButtonLink size="small" href={`/o/${platformId}/request-logs`} startIcon={<ListAltOutlined />}>
          API request logs
        </ButtonLink>
      </Stack>
    );
  };

  return (
    <BaseSettingCard
      title="API keys"
      helpUrl="https://www.usewaypoint.com/docs/api-keys-and-authentication"
      actions={
        <PermissionTooltip span name="createApiKey">
          <Button disabled={!createApiKeyPermission} onClick={() => navigate(newApiKeyHref)} variant="outlined">
            Add
          </Button>
        </PermissionTooltip>
      }
    >
      {renderChildren()}
    </BaseSettingCard>
  );
}
